import { Modal, Select } from '@/components';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import '@/assets/styles/components/new-topic-modal.styles.scss';
import React, { useMemo, useState } from 'react';
import { SelectOption } from '@/components/select/select.types.ts';
import clsx from 'clsx';
import { ExpertSupervisorChatCreateRequest } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useSizes } from '@/utility/responsive.ts';

interface Props {
	isOpen: boolean;
	onClose: () => void;
}

const NewTopicModal = ({ isOpen, onClose }: Props) => {
	const { isMobile } = useSizes();
	const { ExpertSupervisorChatControllerApi } = useApi();
	const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
	const [message, setMessage] = useState<string>('');
	const queryClient = useQueryClient();
	const { data: topics } = useQueryProvider([QUERY_KEYS.findAllTopics], () => ExpertSupervisorChatControllerApi.findAllTopics(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false
	});

	const sendSupportChat = useMutationProvider((body: ExpertSupervisorChatCreateRequest) => ExpertSupervisorChatControllerApi.createChat(body), {
		onSuccess: () => {
			toast.success('Mesajınız başarıyla gönderildi');
		}
	});

	const onSubmit = async () => {
		try {
			const body: ExpertSupervisorChatCreateRequest = {
				topicId: selectedOption,
				message: message
			};
			await sendSupportChat.mutateAsync(body);
			await queryClient.invalidateQueries(QUERY_KEYS.findAllChats);

			handleClose();
		} catch (error) {
			console.error(error);
		}
	};

	const options: SelectOption[] = useMemo(
		() =>
			topics?.map(topic => ({
				label: topic.description as string,
				value: topic.topicId as string
			})) || [],
		[topics]
	);

	const handleClose = () => {
		setSelectedOption(undefined);
		setMessage('');
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			closeOnEsc={false}
			closeOnClickOutside={false}
			className="new-topic-modal">
			<div
				className="close-icon"
				onClick={handleClose}>
				<i className="icons-close" />
			</div>
			<div className="modal-title">Yeni Konu</div>
			<form>
				<Select
					options={options}
					label="Hangi konuda görüşmek istiyorsunuz?"
					value={selectedOption}
					placeholder="Görüşmek istediğiniz konuyu seçin"
					onChange={e => {
						setSelectedOption(e.target.value);
					}}
				/>
				<div className="input-wrapper">
					<label
						htmlFor="message"
						className="message-title">
						Mesaj
					</label>
					<textarea
						className="message-input"
						placeholder="Mesajınızı buraya yazabilirsiniz..."
						value={message}
						maxLength={500}
						onChange={e => setMessage(e.target.value)}
					/>
					{isMobile && (
						<div className="chat-input-footer">
							<button
								type="button"
								onClick={onSubmit}
								className={clsx('submit-button', {
									disabled: !(selectedOption && message.trim() !== '')
								})}
								disabled={!(selectedOption && message.trim() !== '') || sendSupportChat.isLoading}>
								<i className="icons-plain" />
							</button>
						</div>
					)}
				</div>

				{!isMobile && (
					<button
						type="button"
						onClick={onSubmit}
						className={clsx('submit-button', {
							disabled: !(selectedOption && message.trim() !== '')
						})}
						disabled={!(selectedOption && message.trim() !== '') || sendSupportChat.isLoading}>
						{sendSupportChat.isLoading ? 'Gönderiliyor...' : 'Gönder'}
					</button>
				)}
			</form>
		</Modal>
	);
};

export default NewTopicModal;
